import { useCallback, useEffect, useRef, useState } from "react";

const SITE_KEY = "0x4AAAAAAD3_tFYqMrjDvE_Q";
const READY_TIMEOUT_MS = 10000; // if the widget hasn't rendered by then, treat script as unavailable

/**
 * useTurnstile — mounts a Cloudflare Turnstile widget explicitly via
 * `window.turnstile.render()`. Multiple widgets on the same page each get
 * their own instance.
 * Returns:
 *   - containerRef: attach to an empty <div>
 *   - token: current Turnstile response (empty while unsolved / expired)
 *   - reset(): clears the widget so it can be re-completed
 *   - ready: true once the widget has rendered
 *   - scriptFailed: true if the challenge script never loaded (network / adblock / hostname not allowlisted)
 */
export function useTurnstile() {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [token, setToken] = useState("");
  const [ready, setReady] = useState(false);
  const [scriptFailed, setScriptFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;
    let interval = null;
    let timeoutId = null;

    const render = () => {
      if (cancelled || widgetIdRef.current) return;
      if (typeof window === "undefined" || !window.turnstile) return;
      try {
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: SITE_KEY,
          appearance: "interaction-only",
          theme: "dark",
          callback: (t) => setToken(t),
          "expired-callback": () => setToken(""),
          "error-callback": () => setToken(""),
        });
        setReady(true);
        if (interval) { clearInterval(interval); interval = null; }
        if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.debug("turnstile: render failed", err);
      }
    };

    // Try immediately; if the script hasn't loaded yet, poll briefly.
    render();
    if (!widgetIdRef.current) {
      interval = setInterval(render, 150);
      timeoutId = setTimeout(() => {
        if (cancelled || widgetIdRef.current) return;
        setScriptFailed(true);
        if (interval) { clearInterval(interval); interval = null; }
      }, READY_TIMEOUT_MS);
    }

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (err) { /* ignore */ }
      }
      widgetIdRef.current = null;
    };
  }, []);

  const reset = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      try { window.turnstile.reset(widgetIdRef.current); } catch (err) { /* ignore */ }
    }
    setToken("");
  }, []);

  return { containerRef, token, reset, ready, scriptFailed };
}

// Small reusable fallback banner — render below the Turnstile div in each form.
// Kept as a plain component so the styling stays consistent across forms.
export function TurnstileFallback({ show, className = "" }) {
  if (!show) return null;
  return (
    <p
      role="alert"
      data-testid="turnstile-fallback"
      className={`text-[10px] tracking-[0.14em] uppercase text-[#EF4444] leading-relaxed ${className}`}
    >
      Verification unavailable — try again shortly, or disable your ad-blocker for this page.
    </p>
  );
}
